import React, { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Copy,
  ExternalLink,
  KeyRound,
  Lock,
  LogOut,
  RefreshCw,
  Zap,
} from 'lucide-react';
import { Alert, Badge, PageHeader } from '../ui';
import getConfig from '../../config/environments';

const config = getConfig();
const { gatewayURL } = config.api;
const { webviewAppURL } = config;

/* ── CopyButton ─────────────────────────────────────────────────────────────── */
const CopyButton = ({ value, light = false }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  const handleCopy = useCallback(
    (e) => {
      e.stopPropagation();
      navigator.clipboard.writeText(value).then(() => {
        setCopied(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), 2000);
      });
    },
    [value],
  );
  if (light) {
    return (
      <button
        type="button"
        onClick={handleCopy}
        title={copied ? 'Copied!' : 'Copy'}
        className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium transition-colors border
          ${copied
            ? 'bg-success-50 text-success-700 border-success-200'
            : 'bg-ink-100 text-ink-500 hover:bg-ink-200 hover:text-ink-700 border-transparent'
          }`}
      >
        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Copy'}
      className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors
        ${copied
          ? 'bg-success-900/40 text-success-300 border border-success-700/40'
          : 'bg-white/10 text-ink-400 hover:bg-white/20 hover:text-white border border-transparent'
        }`}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

/* ── Code block ─────────────────────────────────────────────────────────────── */
const CodeBlock = ({ code, language = 'bash', label }) => (
  <div className="rounded-xl overflow-hidden border border-ink-800">
    <div className="flex items-center justify-between px-4 py-2 bg-ink-800 border-b border-ink-700">
      <div className="flex items-center gap-2">
        <div className="flex gap-1">
          <span className="h-2.5 w-2.5 rounded-full bg-ink-600" />
          <span className="h-2.5 w-2.5 rounded-full bg-ink-600" />
          <span className="h-2.5 w-2.5 rounded-full bg-ink-600" />
        </div>
        {label && <span className="text-xs font-mono text-ink-400 ml-1">{label}</span>}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-2xs font-mono text-ink-500 uppercase tracking-wider">{language}</span>
        <CopyButton value={code} />
      </div>
    </div>
    <div className="bg-ink-900 px-5 py-4">
      <pre className="text-xs font-mono text-ink-200 whitespace-pre-wrap break-all leading-relaxed">
        {code}
      </pre>
    </div>
  </div>
);

/* ── Response block ─────────────────────────────────────────────────────────── */
const ResponseBlock = ({ code }) => (
  <div className="rounded-xl overflow-hidden border border-ink-800">
    <div className="flex items-center justify-between px-4 py-2 bg-ink-800 border-b border-ink-700">
      <span className="text-xs font-medium text-ink-400">Response</span>
      <span className="text-2xs font-mono text-ink-500 uppercase tracking-wider">200 OK</span>
    </div>
    <div className="bg-ink-900 px-5 py-4">
      <pre className="text-xs font-mono text-success-400 whitespace-pre-wrap leading-relaxed">
        {code}
      </pre>
    </div>
  </div>
);

/* ── Section wrapper ────────────────────────────────────────────────────────── */
const Section = ({ children, className = '' }) => (
  <div className={`bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden ${className}`}>
    {children}
  </div>
);

const SectionHead = ({ label, title, description, icon: Icon, accent = false }) => (
  <div className={`px-6 py-5 border-b border-ink-100 ${accent ? 'bg-accent-50' : ''}`}>
    {label && (
      <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400 mb-1">{label}</p>
    )}
    <div className="flex items-start gap-3">
      {Icon && (
        <div className="h-8 w-8 rounded-lg bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
          <Icon className="h-4 w-4 text-accent-600" />
        </div>
      )}
      <div>
        <h2 className="text-sm font-semibold text-ink-900">{title}</h2>
        {description && <p className="mt-0.5 text-xs text-ink-500 leading-relaxed">{description}</p>}
      </div>
    </div>
  </div>
);

/* ── Token type card ────────────────────────────────────────────────────────── */
const TokenCard = ({ icon: Icon, title, badge, badgeTone, children }) => (
  <div className="flex-1 rounded-xl border border-ink-200 bg-ink-50 p-5 flex flex-col gap-3 min-w-0">
    <div className="flex items-start justify-between gap-2">
      <div className="h-9 w-9 rounded-lg bg-white border border-ink-200 flex items-center justify-center shrink-0 shadow-card">
        <Icon className="h-4 w-4 text-accent-600" />
      </div>
      <Badge tone={badgeTone}>{badge}</Badge>
    </div>
    <div>
      <p className="text-sm font-semibold text-ink-900">{title}</p>
      <p className="mt-1.5 text-xs text-ink-600 leading-relaxed">{children}</p>
    </div>
  </div>
);

/* ── Flow step ──────────────────────────────────────────────────────────────── */
const FlowStep = ({ index, from, to, method, detail, last = false }) => (
  <div className="relative flex gap-4">
    {/* Connector line */}
    {!last && (
      <div className="absolute left-4 top-8 bottom-0 w-px bg-ink-100" />
    )}
    {/* Step bubble */}
    <div className="h-8 w-8 rounded-full border-2 border-accent-200 bg-white text-accent-600 text-xs font-bold flex items-center justify-center shrink-0 z-10">
      {index}
    </div>
    <div className="flex-1 pb-5 min-w-0">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <span className="text-xs font-semibold text-ink-700">{from}</span>
        <ChevronRight className="h-3 w-3 text-ink-300 shrink-0" />
        <span className="text-xs font-semibold text-ink-700">{to}</span>
        {method && (
          <code className="text-xs font-mono px-2 py-0.5 rounded-md bg-accent-50 text-accent-700 border border-accent-100">
            {method}
          </code>
        )}
      </div>
      <p className="text-xs text-ink-500 font-mono leading-relaxed">{detail}</p>
    </div>
  </div>
);

/* ── Integration step card ──────────────────────────────────────────────────── */
const StepCard = ({ number, title, description, children }) => (
  <Section>
    <div className="px-6 py-5 border-b border-ink-100 flex items-start gap-4">
      <div className="h-8 w-8 rounded-full bg-accent-600 text-white text-sm font-bold flex items-center justify-center shrink-0">
        {number}
      </div>
      <div>
        <h2 className="text-sm font-semibold text-ink-900">{title}</h2>
        {description && <p className="mt-0.5 text-xs text-ink-500">{description}</p>}
      </div>
    </div>
    <div className="px-6 py-5 space-y-3">{children}</div>
  </Section>
);

/* ── Contract note ──────────────────────────────────────────────────────────── */
const ContractNote = ({ children }) => (
  <div className="flex items-start gap-2">
    <Check className="h-3.5 w-3.5 text-accent-500 shrink-0 mt-0.5" />
    <span className="text-xs text-ink-600">{children}</span>
  </div>
);

/* ── Security rule row ──────────────────────────────────────────────────────── */
const SecurityRule = ({ title, body }) => (
  <div className="flex items-start gap-3 py-3.5 border-b border-ink-100 last:border-0">
    <div className="h-5 w-5 rounded-full bg-success-100 flex items-center justify-center shrink-0 mt-0.5">
      <Check className="h-3 w-3 text-success-600" />
    </div>
    <div className="min-w-0">
      <p className="text-xs font-semibold text-ink-800">{title}</p>
      <p className="mt-0.5 text-xs text-ink-500 leading-relaxed">{body}</p>
    </div>
  </div>
);

/* ── Data ───────────────────────────────────────────────────────────────────── */
const TOKEN_CURL = `curl -X POST ${gatewayURL}/v1/auth/token \\
  -H "Content-Type: application/json" \\
  -d '{
    "client_id": "YOUR_CLIENT_ID",
    "client_secret": "YOUR_CLIENT_SECRET"
  }'`;

const TOKEN_RESPONSE = `{
  "access_token": "eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9...",
  "token_type": "Bearer",
  "expires_in": 3600
}`;

const SESSION_CURL = `curl -X POST ${gatewayURL}/v1/auth/session \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{ "customerId": "cust_..." }'`;

const SESSION_RESPONSE = `{
  "userId": "550e8400-e29b-41d4-a716-446655440000",
  "sessionKey": "sess_a1b2c3d4e5f6..."
}`;

const LAUNCH_URL = `${webviewAppURL || 'https://webview.tabadulat.com'}/?session=<sessionKey>`;

const SECURITY_RULES = [
  { title: 'Use HTTPS exclusively', body: 'All calls to the Partner Gateway must use HTTPS. Never make token or session requests over plain HTTP.' },
  { title: 'Keep client_secret server-side', body: 'Your client_secret must never be present in client-side code, app bundles, or public repositories. Rotate it immediately if exposed.' },
  { title: 'Do not log or persist session tokens', body: 'Session tokens are short-lived bearer credentials. Do not write them to logs, databases, or analytics pipelines.' },
  { title: 'Rotate credentials periodically', body: 'Rotate your client_id and client_secret regularly via the API Clients page. Invalidate unused clients.' },
  { title: 'Handle token expiry gracefully', body: 'Implement retry logic for 401 responses: re-obtain an access token and re-mint a session token before surfacing an error to the user.' },
  { title: 'Scope validation', body: 'Session tokens are scoped to your organisation. You cannot generate a session for a customer that belongs to a different organisation.' },
];

const ERROR_ROWS = [
  { code: '400', endpoint: '/v1/auth/token', tone: 'warning', description: 'Missing or malformed client_id / client_secret.' },
  { code: '401', endpoint: '/v1/auth/token', tone: 'danger', description: 'Invalid client credentials. Verify client_id and client_secret.' },
  { code: '401', endpoint: '/v1/auth/session', tone: 'danger', description: 'Missing, expired, or invalid Bearer token.' },
  { code: '403', endpoint: '/v1/auth/session', tone: 'danger', description: 'Token lacks M2M scope, or customer does not belong to the calling organisation.' },
  { code: '404', endpoint: '/v1/auth/session', tone: 'warning', description: 'Customer ID not found.' },
];

const ENV_ROWS = [
  { env: 'Sandbox', tone: 'warning', gateway: 'https://sandbox.api.partners.tabadulat.com', webview: 'https://sandbox.webview.tabadulat.com' },
  { env: 'Production', tone: 'success', gateway: 'https://api.partners.tabadulat.com', webview: 'https://webview.tabadulat.com' },
];

const ERROR_CODE_STYLE = { warning: 'text-warning-700 bg-warning-50 border border-warning-200', danger: 'text-danger-700 bg-danger-50 border border-danger-200' };

/* Partner-facing environment label — internal names (alpha/beta/development) are never shown */
const ENV_LABELS = { alpha: 'Sandbox', beta: 'Sandbox', development: 'Sandbox', production: 'Production' };


/* ── Exit: data ─────────────────────────────────────────────────────────────── */
const EXIT_INTENTS = [
  {
    intent: 'close',
    sent: 'The user finishes with the embedded app (the Close button, then Yes on the confirmation).',
    action: 'Dismiss the whole embedded screen: pop it, finish the activity, or dismiss the modal.',
    meta: 'reason "user", metadata.intent "close"',
  },
  {
    intent: 'done',
    sent: 'A flow completed successfully.',
    action: 'Dismiss the embedded screen, then handle the completion if you need to (refresh balances, show a confirmation).',
    meta: 'reason "completed", metadata.intent "done"',
  },
  {
    intent: 'back',
    sent: 'The user goes back while the embedded app is already at its first screen. It handles its own history first.',
    action: 'Go back one level in your navigation, the same as your own back button on a pushed screen.',
    meta: 'reason "user", metadata.intent "back"',
  },
];

const EXIT_REQUEST = `{
  "v": "2.0",
  "id": "r_8f3a1c2d9b",
  "cmd": "exit",
  "payload": { "reason": "user", "metadata": { "intent": "close" } }
}`;

const EXIT_ACK = `{ "v": "2.0", "replyTo": "r_8f3a1c2d9b", "ok": true, "result": { "handled": true } }`;

const EXIT_REFUSE = `{ "v": "2.0", "replyTo": "r_8f3a1c2d9b", "ok": false, "error": { "code": "REJECTED" } }`;

const HANDSHAKE_JSON = `{
  "contract": "2.0",
  "hostType": "your-app",
  "transport": "rn",
  "capabilities": {
    "exit": true, "exitReturnTo": [], "navigate": [], "back": true,
    "session": false, "haptics": false, "externalUrl": false, "document": false,
    "orientation": false, "lifecycle": false, "keyboard": false, "insets": true
  },
  "lang": "en",
  "insets": { "top": 59, "right": 0, "bottom": 34, "left": 0 },
  "window": { "width": 393, "height": 852 }
}`;

const BACK_EVENT = `{ "v": "2.0", "event": "back", "payload": {} }`;

const HOST_DUTIES = [
  'Accept messages only from the embedded app’s origin and main frame, no larger than 4096 bytes, and only from a 2.x protocol version.',
  'Handle only the commands you declared in the handshake. Answer anything else with UNSUPPORTED and ignore malformed input.',
  'Acknowledge every request that has an id, exactly once, with handled: true once you start the navigation. The embedded app locks its screen after an acknowledged exit.',
  'Navigate on your side: close and done dismiss the whole embedded screen, back pops one level.',
  'Make exit idempotent: handle a request id once and ignore further exit requests while you are already dismissing (a double tap sends one request, but be safe).',
  'Never log request payloads. Log the id, the command and the outcome.',
];

const PLATFORM_GUIDES = [
  {
    id: 'react-native',
    label: 'React Native',
    note: 'The page posts its messages to your message handler. Inject the handshake before the page loads, validate each message, acknowledge exit, then dismiss the screen. Forward the Android hardware back button to the page.',
    code: `// Props of the web view component that shows the launch URL
//   injectedJavaScriptBeforeContentLoaded={'window.__TABADULAT_HOST__ = ' + JSON.stringify(handshake) + '; true;'}
//   onMessage={onMessage}

const send = (message) =>
  webViewRef.current?.injectJavaScript(
    'window.__tabadulat_receive(' + JSON.stringify(JSON.stringify({ v: '2.0', ...message })) + '); true;',
  );

const onMessage = (event) => {
  let msg;
  try { msg = JSON.parse(event.nativeEvent.data); } catch { return; }       // malformed: ignore
  if (!msg.v?.startsWith('2.') || !msg.id) return;                          // wrong version or a notification
  if (msg.cmd === 'exit') {
    send({ replyTo: msg.id, ok: true, result: { handled: true } });         // acknowledge first
    navigation.goBack();                                                    // then dismiss the screen
  } else if (msg.cmd === 'ready') {
    send({ replyTo: msg.id, ok: true, result: {} });
  } else {
    send({ replyTo: msg.id, ok: false, error: { code: 'UNSUPPORTED' } });
  }
};

// Android hardware back: let the embedded app decide (it asks you to exit only at its first screen)
BackHandler.addEventListener('hardwareBackPress', () => {
  send({ event: 'back', payload: {} });
  return true;
});`,
    language: 'javascript',
  },
  {
    id: 'ios',
    label: 'iOS',
    note: 'Register one script message handler named "tabadulat" and inject the handshake at document start for the main frame only. Disable the interactive pop gesture while the embedded app has its own history (it reports this with nav.state).',
    code: String.raw`// At document start, main frame only
let handshake = WKUserScript(source: "window.__TABADULAT_HOST__ = <handshake JSON>;",
                             injectionTime: .atDocumentStart, forMainFrameOnly: true)
controller.addUserScript(handshake)
controller.addScriptMessageHandler(TabadulatBridge(), contentWorld: .page, name: "tabadulat")

final class TabadulatBridge: NSObject, WKScriptMessageHandlerWithReply {
    func userContentController(_ c: WKUserContentController, didReceive message: WKScriptMessage,
                               replyHandler: @escaping (Any?, String?) -> Void) {
        guard message.frameInfo.isMainFrame,
              message.frameInfo.securityOrigin.host == "your-embed-host",          // origin check
              let raw = message.body as? String, raw.utf8.count <= 4096,
              let json = try? JSONSerialization.jsonObject(with: Data(raw.utf8)) as? [String: Any],
              (json["v"] as? String)?.hasPrefix("2.") == true,
              let id = json["id"] as? String else { return replyHandler(nil, nil) }

        if json["cmd"] as? String == "exit" {
            send(["replyTo": id, "ok": true, "result": ["handled": true]])        // acknowledge first
            DispatchQueue.main.async { self.dismissEmbeddedScreen() }              // then dismiss
        } else {
            send(["replyTo": id, "ok": false, "error": ["code": "UNSUPPORTED"]])
        }
        replyHandler(nil, nil)
    }
}
// send(_:) evaluates: window.__tabadulat_receive(<the JSON message as a string literal>)`,
    language: 'swift',
  },
  {
    id: 'android',
    label: 'Android',
    note: 'Use an origin-scoped web message listener, not a global JavaScript interface. Inject the handshake at document start for your embed origin only. Forward system back (including predictive back) to the page.',
    code: String.raw`val origins = setOf("https://your-embed-host")

WebViewCompat.addDocumentStartJavaScript(webView, "window.__TABADULAT_HOST__ = <handshake JSON>;", origins)

WebViewCompat.addWebMessageListener(webView, "tabadulatHost", origins) { _, message, _, isMainFrame, reply ->
    if (!isMainFrame) return@addWebMessageListener
    val json = runCatching { JSONObject(message.data ?: return@addWebMessageListener) }.getOrNull() ?: return@addWebMessageListener
    if (!json.optString("v").startsWith("2.") || !json.has("id")) return@addWebMessageListener
    if (json.optString("cmd") == "exit") {
        reply.postMessage("{\"v\":\"2.0\",\"replyTo\":\"" + json.getString("id") + "\",\"ok\":true,\"result\":{\"handled\":true}}")  // acknowledge first
        runOnUiThread { finish() }                                                                                                   // then dismiss
    }
}

// System back: hand it to the page
onBackPressedDispatcher.addCallback(this) {
    webView.evaluateJavascript("window.__tabadulat_receive(...)", null)   // argument: the back event below, as a string literal
}`,
    language: 'kotlin',
  },
  {
    id: 'flutter',
    label: 'Flutter',
    note: 'Register a JavaScript channel named "tabadulatHost". No injection is needed: the embedded app asks "hello" and you answer with the handshake. Forward the system back to the page the same way.',
    code: `controller.addJavaScriptChannel('tabadulatHost', onMessageReceived: (m) {
  if (m.message.length > 4096) return;
  final msg = jsonDecode(m.message) as Map<String, dynamic>;
  if (!(msg['v'] as String).startsWith('2.') || msg['id'] == null) return;

  switch (msg['cmd']) {
    case 'hello':                                   // no injection here: answer the handshake instead
      reply(msg['id'], handshake);
    case 'exit':
      reply(msg['id'], {'handled': true});          // acknowledge first
      Navigator.of(context).pop();                  // then dismiss the screen
    default:
      replyError(msg['id'], 'UNSUPPORTED');
  }
});

// reply(id, result) runs: window.__tabadulat_receive(<jsonEncode of the reply message>)`,
    language: 'dart',
  },
];

const LIFECYCLE_ROWS = [
  { situation: 'The page reloads or the screen is recreated', behaviour: 'The handshake runs again on every page load. Nothing is remembered from an earlier page. When you present the embedded screen again, start from a clean exit state.' },
  { situation: 'A request arrives before you are ready', behaviour: 'The embedded app waits for the handshake and never sends early. If no host answers within a couple of seconds it treats the request as unhandled and stays usable.' },
  { situation: 'You do not acknowledge, or the screen stays alive', behaviour: 'No reply within 1.5 s counts as failed. If you said handled but the page is still alive 3 s later, it recovers and unlocks its screen.' },
  { situation: 'The screen is destroyed during a request', behaviour: 'Pending requests settle as unhandled. Nothing hangs and nothing throws.' },
  { situation: 'The embedded app runs outside a host (a browser)', behaviour: 'Close reports that no host is available and the page stays where it is. It is never an error.' },
];

const ExitPlatformExamples = () => {
  const [active, setActive] = useState(PLATFORM_GUIDES[0].id);
  const guide = PLATFORM_GUIDES.find((g) => g.id === active);
  return (
    <div className="space-y-3">
      <div role="tablist" aria-label="Platform" className="flex flex-wrap gap-2">
        {PLATFORM_GUIDES.map((g) => (
          <button
            key={g.id}
            type="button"
            role="tab"
            aria-selected={g.id === active}
            onClick={() => setActive(g.id)}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold border transition-colors ${
              g.id === active
                ? 'bg-accent-600 text-white border-accent-600'
                : 'bg-white text-ink-600 border-ink-200 hover:bg-ink-50'
            }`}
          >
            {g.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-ink-600 leading-relaxed">{guide.note}</p>
      <CodeBlock code={guide.code} language={guide.language} label={`${guide.label} host`} />
    </div>
  );
};

/* ── Component ──────────────────────────────────────────────────────────────── */
const EmbeddedAppGuide = () => {
  const envLabel = ENV_LABELS[config.environment] || 'Production';

  return (
    <div className="space-y-6 max-w-4xl">

      {/* Header */}
      <PageHeader
        title="Embedded App"
        description="Authenticate and deliver a hosted trading experience to your customers using short-lived session tokens."
        breadcrumb={['Developers', 'Embedded App']}
        actions={<Badge tone="info">{envLabel}</Badge>}
      />

      {/* Security banner */}
      <div className="flex gap-3 rounded-xl border border-warning-200 bg-warning-50 px-5 py-4">
        <div className="h-8 w-8 rounded-lg bg-warning-100 flex items-center justify-center shrink-0">
          <AlertTriangle className="h-4 w-4 text-warning-600" />
        </div>
        <div>
          <p className="text-sm font-semibold text-warning-900">Backend-only operations</p>
          <p className="mt-0.5 text-xs text-warning-700 leading-relaxed">
            All token and session requests must originate from your backend. Never expose your{' '}
            <code className="bg-warning-100 px-1 rounded font-mono">client_secret</code> or
            system-level access token in client-side code, app bundles, or public repositories.
          </p>
        </div>
      </div>

      {/* Overview */}
      <Section>
        <SectionHead title="Overview" />
        <div className="px-6 py-5 space-y-2 text-sm text-ink-600 leading-relaxed">
          <p>
            Tabadulat offers a hosted, embeddable trading experience authenticated by short-lived session tokens.
            Your backend exchanges its M2M credentials for a session token, then constructs a launch URL and hands
            it to the client surface that will host the experience. How that surface renders the URL is entirely
            up to you.
          </p>
          <p>
            The session token is <strong className="text-ink-800">single-use</strong> and expires in approximately{' '}
            <strong className="text-ink-800">60 seconds</strong> — long enough to bootstrap the embedded experience,
            but too short to be reused or leaked.
          </p>
        </div>
      </Section>

      {/* Token types — side by side */}
      <div>
        <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400 mb-3 px-1">Token types</p>
        <div className="flex flex-col sm:flex-row gap-4">
          <TokenCard icon={KeyRound} title="System-level access token" badge="Long-lived" badgeTone="neutral">
            Long-lived, server-to-server credential obtained via{' '}
            <code className="bg-ink-200 px-1 rounded font-mono text-ink-700">POST /v1/auth/token</code>{' '}
            using your <code className="bg-ink-200 px-1 rounded font-mono text-ink-700">client_id</code> and{' '}
            <code className="bg-ink-200 px-1 rounded font-mono text-ink-700">client_secret</code>. Used to
            authorise API calls and to mint session tokens. Treat it as a privileged credential.
          </TokenCard>
          <TokenCard icon={Zap} title="Session token" badge="~60s · Single-use" badgeTone="warning">
            Short-lived, single-use token tied to a specific customer. Obtained via{' '}
            <code className="bg-ink-200 px-1 rounded font-mono text-ink-700">POST /v1/auth/session</code>{' '}
            using your system-level token. Passed as the{' '}
            <code className="bg-ink-200 px-1 rounded font-mono text-ink-700">?session=</code> parameter in
            the launch URL. Not refreshable — request a new one for each embed.
          </TokenCard>
        </div>
      </div>

      {/* Auth flow */}
      <Section>
        <SectionHead
          label="Integration flow"
          title="Authentication sequence"
          description="End-to-end flow from your backend to the embedded surface."
        />
        <div className="px-6 py-5">
          <FlowStep
            index={1}
            from="Your backend"
            to="Partner Gateway"
            method="POST /v1/auth/token"
            detail="client_id + client_secret → access_token (Bearer)"
          />
          <FlowStep
            index={2}
            from="Your backend"
            to="Partner Gateway"
            method="POST /v1/auth/session"
            detail="Bearer access_token + customerId → { userId, sessionKey }"
          />
          <FlowStep
            index={3}
            from="Your backend"
            to="Client"
            method={null}
            detail={`Construct launch URL: ${webviewAppURL || 'https://webview.tabadulat.com'}/?session=<sessionKey>`}
          />
          <FlowStep
            index={4}
            from="Embedded surface"
            to="Tabadulat"
            method={null}
            detail="sessionKey exchanged → authenticated user session, ready to trade"
            last
          />
        </div>
      </Section>

      {/* Steps */}
      <StepCard
        number="1"
        title="Obtain a system-level access token"
        description="Use your client credentials to obtain a long-lived access token. This call must happen on your backend."
      >
        <CodeBlock code={TOKEN_CURL} language="bash" label="POST /v1/auth/token" />
        <ResponseBlock code={TOKEN_RESPONSE} />
        <p className="text-xs text-ink-500 leading-relaxed">
          Cache the <code className="bg-ink-100 px-1 rounded font-mono">access_token</code> server-side and reuse it
          across session requests. It is valid for <code className="bg-ink-100 px-1 rounded font-mono">expires_in</code>{' '}
          seconds. Request a new one when it expires.
        </p>
      </StepCard>

      <StepCard
        number="2"
        title="Generate a session token for a customer"
        description="Exchange the access token and a customer ID for a short-lived, single-use session token."
      >
        <CodeBlock code={SESSION_CURL} language="bash" label="POST /v1/auth/session" />
        <ResponseBlock code={SESSION_RESPONSE} />
        <div className="rounded-xl border border-info-100 bg-info-50 px-5 py-4 space-y-2">
          <p className="text-xs font-semibold text-info-900">Session token contract</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
            <ContractNote>Single-use — bootstraps exactly one session.</ContractNote>
            <ContractNote>Expires in ~60 seconds from issuance.</ContractNote>
            <ContractNote>Not refreshable — mint a new one per embed.</ContractNote>
            <ContractNote>Org-scoped — cross-tenant usage is rejected.</ContractNote>
          </div>
        </div>
      </StepCard>

      <StepCard
        number="3"
        title="Construct and deliver the launch URL"
        description="Append the sessionKey as the ?session= query parameter. Return this URL to the client that will host the embedded experience."
      >
        <div className="rounded-xl overflow-hidden border border-ink-800">
          <div className="flex items-center justify-between px-4 py-2 bg-ink-800 border-b border-ink-700">
            <span className="text-xs font-medium text-ink-400">Launch URL</span>
            <CopyButton value={LAUNCH_URL} />
          </div>
          <div className="bg-ink-900 px-5 py-4">
            <pre className="text-xs font-mono text-success-400 whitespace-pre-wrap break-all leading-relaxed">
              {LAUNCH_URL}
            </pre>
          </div>
        </div>
        <Alert tone="info">
          The embedded surface loads this URL directly.{' '}
          The <code className="bg-info-100 px-1 rounded font-mono text-xs">sessionKey</code> is consumed on first load —
          no further authentication is required from the client side.
        </Alert>
      </StepCard>

      {/* Session lifecycle */}
      <Section>
        <SectionHead
          title="Session lifecycle"
          description="Normal flow and what to do when a session expires."
          icon={RefreshCw}
        />
        <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-xs font-semibold text-ink-700 mb-3">Normal flow</p>
            <ol className="space-y-2.5">
              {[
                'Customer opens the embedded experience in your product.',
                'Your backend calls POST /v1/auth/session to mint a session token.',
                'Your backend returns the launch URL to the client.',
                'Embedded surface loads the URL — session consumed, user authenticated.',
                'Session remains active as long as the surface maintains it.',
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="h-5 w-5 rounded-full bg-accent-100 text-accent-700 text-2xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-xs text-ink-600 leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </div>
          <div>
            <p className="text-xs font-semibold text-ink-700 mb-3">Session expired or auth failure</p>
            <div className="rounded-xl border border-warning-100 bg-warning-50 p-4 text-xs text-warning-800 leading-relaxed space-y-2">
              <p>
                If the embedded surface reports an authentication failure (session token expired before
                consumption, or the active session was invalidated), your backend should:
              </p>
              <ol className="space-y-1.5 ml-1">
                <li className="flex items-start gap-2">
                  <span className="font-bold text-warning-600 shrink-0">1.</span>
                  Request a fresh session token via <code className="bg-warning-100 px-1 rounded font-mono">POST /v1/auth/session</code>.
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-warning-600 shrink-0">2.</span>
                  Deliver a new launch URL to the client.
                </li>
              </ol>
              <p className="text-2xs text-warning-700">
                Never attempt to reuse or cache a session token across multiple embeds.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Exiting the embedded app */}
      <Section>
        <SectionHead
          label="Host integration"
          title="Exiting the embedded app"
          description="The embedded app cannot close itself. It asks your app to leave, and your app dismisses the screen."
          icon={LogOut}
        />
        <div className="px-6 py-5 space-y-6">
          <Alert tone="info">
            A web page cannot close the view that hosts it, and it never touches your navigation stack. When the user
            taps <strong>Close</strong> and confirms, the embedded app sends your app one message and waits for your
            reply. <strong>Your app owns the actual dismissal</strong>: pop the screen, finish the activity or dismiss
            the modal, whichever fits your navigation.
          </Alert>

          <div>
            <p className="text-xs font-semibold text-ink-700 mb-3">The three intents</p>
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr>
                    <th className="w-20">Intent</th>
                    <th>When it is sent</th>
                    <th>What your app should do</th>
                  </tr>
                </thead>
                <tbody>
                  {EXIT_INTENTS.map((row) => (
                    <tr key={row.intent}>
                      <td>
                        <code className="text-xs font-mono px-2 py-0.5 rounded-md bg-accent-50 text-accent-700 border border-accent-100">
                          {row.intent}
                        </code>
                      </td>
                      <td className="text-xs text-ink-600">{row.sent}</td>
                      <td className="text-xs text-ink-600">
                        {row.action}
                        <span className="block mt-1 text-2xs font-mono text-ink-400">{row.meta}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-ink-500 leading-relaxed">
              All three travel as one <code className="bg-ink-100 px-1 rounded font-mono">exit</code> request. Exiting
              means leaving the embedded experience, never going back in browser history: with web history A, B, C,
              close goes from C to your app, not to B.
            </p>
          </div>

          <div className="space-y-3">
            <p className="text-xs font-semibold text-ink-700">The messages</p>
            <p className="text-xs text-ink-600 leading-relaxed">
              Messages are JSON strings. The protocol version is <code className="bg-ink-100 px-1 rounded font-mono">2.0</code>;
              accept any <code className="bg-ink-100 px-1 rounded font-mono">2.x</code> and reject other major versions.
              The embedded app sends a request with an <code className="bg-ink-100 px-1 rounded font-mono">id</code>, and
              you answer it exactly once with the same id. New capabilities are added as new commands or optional fields,
              so existing integrations keep working.
            </p>
            <CodeBlock code={EXIT_REQUEST} language="json" label="Embedded app → your app" />
            <CodeBlock code={EXIT_ACK} language="json" label="Your app → embedded app (accepted)" />
            <CodeBlock code={EXIT_REFUSE} language="json" label="Your app → embedded app (declined)" />
            <p className="text-xs text-ink-500 leading-relaxed">
              Your replies, and events such as back, are delivered to the page by calling{' '}
              <code className="bg-ink-100 px-1 rounded font-mono">window.__tabadulat_receive(json)</code> with the
              message as a string.
            </p>
          </div>

          <div>
            <p className="text-xs font-semibold text-ink-700 mb-3">What your app must do</p>
            <div className="space-y-2">
              {HOST_DUTIES.map((duty) => (
                <ContractNote key={duty}>{duty}</ContractNote>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-xs font-semibold text-ink-700">Tell the embedded app about its host</p>
            <p className="text-xs text-ink-600 leading-relaxed">
              Before the page loads, inject a handshake as{' '}
              <code className="bg-ink-100 px-1 rounded font-mono">window.__TABADULAT_HOST__</code>. It declares what you
              support (only <code className="bg-ink-100 px-1 rounded font-mono">exit</code> is required) and gives the
              safe-area insets and window size so the embedded header sits correctly under your status bar. The
              embedded app never calls something you did not declare. Where injection at document start is not
              available, answer a <code className="bg-ink-100 px-1 rounded font-mono">hello</code> request with the same object.
              Set <code className="bg-ink-100 px-1 rounded font-mono">transport</code> to{' '}
              <code className="bg-ink-100 px-1 rounded font-mono">"rn"</code>,{' '}
              <code className="bg-ink-100 px-1 rounded font-mono">"webkit"</code> or{' '}
              <code className="bg-ink-100 px-1 rounded font-mono">"android"</code> to match how your app receives messages,
              or leave it out.
            </p>
            <CodeBlock code={HANDSHAKE_JSON} language="json" label="Handshake" />
          </div>

          <div className="space-y-3">
            <p className="text-xs font-semibold text-ink-700">Examples by platform</p>
            <ExitPlatformExamples />
          </div>

          <div className="space-y-3">
            <p className="text-xs font-semibold text-ink-700">Your back button and system gestures</p>
            <p className="text-xs text-ink-600 leading-relaxed">
              Send your own back (the Android system back button, predictive back, a hardware key) to the embedded app as an
              event. It treats it like its own back button: it closes an open sheet or returns to the previous screen, and
              asks you to exit only when it is already at its first screen. It also tells you when it has internal history
              (a <code className="bg-ink-100 px-1 rounded font-mono">nav.state</code> notification with{' '}
              <code className="bg-ink-100 px-1 rounded font-mono">isRoot</code>), so you can disable an iOS swipe-back gesture
              that would otherwise leave the embedded app instead of going back one screen.
            </p>
            <CodeBlock code={BACK_EVENT} language="json" label="Your app → embedded app" />
          </div>

          <div>
            <p className="text-xs font-semibold text-ink-700 mb-3">Lifecycle and edge cases</p>
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr>
                    <th>Situation</th>
                    <th>What happens</th>
                  </tr>
                </thead>
                <tbody>
                  {LIFECYCLE_ROWS.map((row) => (
                    <tr key={row.situation}>
                      <td className="text-xs font-medium text-ink-700">{row.situation}</td>
                      <td className="text-xs text-ink-600">{row.behaviour}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Alert tone="warning">
            <strong>Migrating from an earlier integration.</strong> Closing through{' '}
            <code className="bg-warning-100 px-1 rounded font-mono text-xs">window.close()</code>, a custom link scheme or
            a deep link does not work reliably in embedded views, so remove it. The earlier plain-string signal
            (<code className="bg-warning-100 px-1 rounded font-mono text-xs">tabadulat_exit</code>, enabled with{' '}
            <code className="bg-warning-100 px-1 rounded font-mono text-xs">?navigation=tabadulat_exit</code>) is{' '}
            <strong>no longer supported</strong>: the embedded app ignores the parameter and never sends the string.
            Handle the messages above instead.
          </Alert>

          <div className="rounded-xl border border-info-100 bg-info-50 px-5 py-4 space-y-2">
            <p className="text-xs font-semibold text-info-900">Before you release</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
              <ContractNote>Close and done dismiss the whole screen exactly once, double tap included.</ContractNote>
              <ContractNote>Back pops one level; the Android back button and the iOS gesture behave.</ContractNote>
              <ContractNote>Reloading the embedded screen and presenting it again still exits.</ContractNote>
              <ContractNote>A browser or offline session fails gracefully, with no crash or stuck screen.</ContractNote>
            </div>
            <p className="text-xs text-info-800 leading-relaxed pt-1">
              Tabadulat provides host kits for React Native, iOS and Android that implement everything on this page
              (validation, acknowledgement, idempotency, back handling and lifecycle). Contact your Tabadulat integration
              contact for access.
            </p>
          </div>
        </div>
      </Section>

      {/* Security */}
      <Section>
        <SectionHead
          title="Security requirements"
          description="These requirements must be met by all integrations."
          icon={Lock}
        />
        <div className="px-6 py-2">
          {SECURITY_RULES.map((r) => (
            <SecurityRule key={r.title} title={r.title} body={r.body} />
          ))}
        </div>
        <div className="px-6 py-4 bg-ink-50 border-t border-ink-100 rounded-b-xl">
          <p className="text-xs text-ink-500">
            Manage and rotate your credentials on the{' '}
            <Link to="/api-clients" className="text-accent-600 hover:underline font-medium">
              API Clients
            </Link>{' '}
            page.
          </p>
        </div>
      </Section>

      {/* Error reference */}
      <Section>
        <SectionHead
          title="Error reference"
          description="Common error responses from the token and session endpoints."
        />
        <div className="overflow-x-auto">
          <table className="table w-full">
            <thead>
              <tr>
                <th className="w-20">Code</th>
                <th>Endpoint</th>
                <th>Meaning</th>
              </tr>
            </thead>
            <tbody>
              {ERROR_ROWS.map((r, i) => (
                <tr key={i}>
                  <td>
                    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-mono font-semibold ${ERROR_CODE_STYLE[r.tone]}`}>
                      {r.code}
                    </span>
                  </td>
                  <td>
                    <code className="text-xs font-mono text-ink-600 bg-ink-50 px-2 py-0.5 rounded border border-ink-100">
                      {r.endpoint}
                    </code>
                  </td>
                  <td className="text-xs text-ink-600">{r.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Environments */}
      <Section>
        <SectionHead
          title="Environments"
          description="Base URLs for each environment. Substitute these in the examples above."
        />
        <div className="overflow-x-auto">
          <table className="table w-full">
            <thead>
              <tr>
                <th className="w-28">Environment</th>
                <th>Partner Gateway</th>
                <th>Embedded App</th>
              </tr>
            </thead>
            <tbody>
              {ENV_ROWS.map((r) => (
                <tr key={r.env}>
                  <td>
                    <Badge tone={r.tone}>{r.env}</Badge>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <code className="text-xs font-mono text-ink-700">{r.gateway}</code>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => navigator.clipboard.writeText(r.gateway)}
                          className="text-ink-400 hover:text-ink-700 transition-colors"
                          title="Copy"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        <a
                          href={`${r.gateway}/redoc`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-ink-400 hover:text-accent-600 transition-colors"
                          title="API Reference"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <code className="text-xs font-mono text-ink-700">{r.webview}</code>
                      <button
                        type="button"
                        onClick={() => navigator.clipboard.writeText(r.webview)}
                        className="text-ink-400 hover:text-ink-700 transition-colors shrink-0"
                        title="Copy"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

    </div>
  );
};

export default EmbeddedAppGuide;
